import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChatCircleText, Megaphone, PaperPlaneTilt, CaretRight, CaretDown, CaretUp } from "@phosphor-icons/react";

const MGMT_ROLES = ["owner"];
function ts(iso) { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Pacific/Honolulu" }); }

export default function CommWidget({ onOpen, refreshKey, collapsed, onToggleCollapse }) {
  const { user } = useAuth();
  const mgmt = MGMT_ROLES.includes(user?.role);
  const [broadcasts, setBroadcasts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/comm/broadcasts");
      setBroadcasts(data || []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post("/comm/messages", mgmt ? { body, scope: "broadcast" } : { body, scope: "direct" });
      setText("");
      toast.success(mgmt ? "Announcement sent to team" : "Sent to management");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const recentBroadcasts = [...broadcasts].slice(-3).reverse();

  return (
    <div data-testid="comm-widget" className="rounded-2xl border-2 border-black bg-[#E0F2FE] hard-shadow-sm p-5 mb-8">
      <div className={`flex items-center justify-between ${collapsed ? "" : "mb-4"}`}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><ChatCircleText size={18} weight="fill" color="#fff" /></div>
          <h3 className="font-display text-lg font-bold">Messages</h3>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="comm-widget-open-btn" onClick={onOpen} className="press text-xs font-bold text-primary flex items-center gap-1">Open <CaretRight size={12} weight="bold" /></button>
          {onToggleCollapse && (
            <button data-testid="collapse-messages" onClick={onToggleCollapse} title={collapsed ? "Expand" : "Collapse"} className="press w-8 h-8 rounded-full border-2 border-black bg-white flex items-center justify-center shrink-0">
              {collapsed ? <CaretDown size={15} weight="bold" /> : <CaretUp size={15} weight="bold" />}
            </button>
          )}
        </div>
      </div>

      {!collapsed && (<>
      {/* Announcements */}
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-black/40 flex items-center gap-1 mb-2"><Megaphone size={13} weight="bold" /> Announcements</p>
      {recentBroadcasts.length === 0 ? (
        <p className="text-sm text-black/40 mb-3">No announcements yet.</p>
      ) : (
        <div className="space-y-2 mb-3">
          {recentBroadcasts.map((m) => (
            <div key={m.id} data-testid={`comm-widget-broadcast-${m.id}`} className="rounded-xl border-2 border-black bg-[#FEF9C3] p-3">
              <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
              <p className="text-[10px] text-black/40 mt-1">{m.sender_name} · {ts(m.created_at)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Quick composer — management broadcasts; employees reply via the full panel */}
      {mgmt ? (
        <div className="flex gap-2">
          <input data-testid="comm-widget-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Announce to all employees…"
            className="flex-1 h-11 px-3 rounded-full border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" />
          <button data-testid="comm-widget-send-btn" onClick={send} disabled={busy || !text.trim()} className="press w-11 h-11 rounded-full bg-primary text-white border-2 border-black flex items-center justify-center disabled:opacity-50"><PaperPlaneTilt size={18} weight="fill" /></button>
        </div>
      ) : (
        <button data-testid="comm-widget-message-btn" onClick={onOpen} className="press w-full h-11 rounded-full bg-white text-foreground font-bold border-2 border-black text-sm flex items-center justify-center gap-2"><PaperPlaneTilt size={16} weight="fill" /> Message management</button>
      )}
      </>)}
    </div>
  );
}
